import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiService, User, AuthResponse } from '../services/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<AuthResponse>;
  register: (username: string, email: string, password: string) => Promise<AuthResponse>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Check if user is authenticated on app load
  useEffect(() => {
    const initAuth = async () => {
      console.log('🔄 Initializing auth...');
      
      if (apiService.isAuthenticated()) {
        console.log('✅ Token found, fetching user profile...');
        try {
          const userProfile = await apiService.getProfile();
          console.log('✅ User profile loaded:', userProfile);
          setUser(userProfile);
        } catch (error) {
          console.error('❌ Failed to load user profile:', error);
          console.log('🔄 Clearing invalid token...');
          apiService.logout(); // Clear invalid token
          setUser(null);
        }
      } else {
        console.log('ℹ️ No authentication token found');
        setUser(null);
      }
      
      setLoading(false);
      console.log('✅ Auth initialization complete');
    };

    initAuth();
  }, []);

  const login = async (username: string, password: string): Promise<AuthResponse> => {
    try {
      const response = await apiService.login(username, password);
      
      // Handle suspended user login - they get logged in but with suspension flag
      if (response.suspended && response.success && response.user) {
        setUser(response.user); // Set user so they're logged in
        return {
          success: true,
          suspended: true,
          suspensionReason: response.suspensionReason,
          adminMessage: response.adminMessage,
          token: response.token,
          user: response.user,
          message: 'Logged in as suspended user'
        };
      }
      
      if (response.user) {
        setUser(response.user);
      }
      return { success: true, token: response.token, user: response.user, message: response.message };
    } catch (error) {
      console.error('Login failed:', error);
      return { 
        success: false, 
        message: error instanceof Error ? error.message : 'Login failed'
      };
    }
  };

  const register = async (username: string, email: string, password: string): Promise<AuthResponse> => {
    try {
      const response = await apiService.register(username, email, password);
      
      // New flow: don't set user immediately, wait for email verification
      if (response.requiresVerification) {
        return { 
          success: true, 
          requiresVerification: true,
          userId: response.userId,
          message: response.message 
        };
      }
      
      // Old flow: immediate login (for backward compatibility)
      if (response.user) {
        setUser(response.user);
        return { success: true, token: response.token, user: response.user, message: response.message };
      }
      
      return { success: true, message: response.message };
    } catch (error) {
      console.error('Registration failed:', error);
      // Try to parse error response for conflict information
      const errorMessage = error instanceof Error ? error.message : 'Registration failed';
      
      // Check if error message contains conflict info (from API error response)
      let conflictType: 'username' | 'email' | undefined = undefined;
      let existingUsername: string | undefined = undefined;
      
      try {
        // If the error came from the API with JSON, it might have additional info
        if (error instanceof Error && error.message.includes('Username already exists')) {
          conflictType = 'username';
        } else if (error instanceof Error && error.message.includes('Email already exists')) {
          conflictType = 'email';
        }
      } catch (e) {
        // Ignore parsing errors
      }
      
      return { 
        success: false, 
        message: errorMessage,
        conflictType,
        existingUsername
      };
    }
  };

  const logout = () => {
    apiService.logout();
    setUser(null);
  };

  const value: AuthContextType = {
    user,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};